import type { DownloadAssetRequest } from '../context/PlatformContext'
import { isRemoteImageUrl } from './image-preview'

const DATA_URL_EXTENSION_MAP: Record<string, string> = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/jpg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
  'image/bmp': '.bmp',
  'image/x-icon': '.ico',
  'image/vnd.microsoft.icon': '.ico',
  'image/avif': '.avif',
}

function sanitizeFilenamePart(value: string | undefined): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null

  const sanitized = trimmed
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
    .replace(/\s+/g, ' ')
    .trim()

  return sanitized || null
}

function getFilenameFromPathLike(value: string | undefined): string | null {
  if (!value) return null

  if (/^https?:\/\//i.test(value)) {
    try {
      const pathname = new URL(value).pathname
      const segment = pathname.split('/').filter(Boolean).pop()
      return sanitizeFilenamePart(segment ? decodeURIComponent(segment) : undefined)
    } catch {
      return null
    }
  }

  const normalized = value.replace(/\\/g, '/')
  const segment = normalized.split('/').filter(Boolean).pop()
  return sanitizeFilenamePart(segment)
}

function getExtensionFromPathLike(value: string | undefined): string | null {
  const filename = getFilenameFromPathLike(value)
  if (!filename) return null
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex <= 0 || dotIndex === filename.length - 1) return null
  return filename.slice(dotIndex)
}

function getExtensionFromDataUrl(dataUrl: string | undefined): string | null {
  if (!dataUrl?.startsWith('data:')) return null
  const mimeMatch = dataUrl.match(/^data:([^;,]+)/i)
  if (!mimeMatch?.[1]) return null
  return DATA_URL_EXTENSION_MAP[mimeMatch[1].toLowerCase()] ?? null
}

function ensureFilenameExtension(filename: string, extension: string | null): string {
  if (!extension) return filename
  const dotIndex = filename.lastIndexOf('.')
  if (dotIndex > 0 && dotIndex < filename.length - 1) return filename
  return `${filename}${extension}`
}

export function createImageDownloadRequest(
  src: string,
  options: {
    renderedSrc?: string
    label?: string
  } = {},
): DownloadAssetRequest {
  const request: DownloadAssetRequest = {
    suggestedName: options.label,
  }

  if (src.startsWith('data:')) {
    request.dataUrl = src
  } else if (isRemoteImageUrl(src)) {
    request.sourceUrl = src
  } else {
    request.sourcePath = src
  }

  if (options.renderedSrc?.startsWith('data:')) {
    request.dataUrl = options.renderedSrc
  } else if (!request.sourceUrl && options.renderedSrc && isRemoteImageUrl(options.renderedSrc)) {
    request.sourceUrl = options.renderedSrc
  }

  return request
}

export function inferDownloadFilename(
  request: DownloadAssetRequest,
  fallbackBaseName = 'image',
): string {
  const suggestedName = sanitizeFilenamePart(request.suggestedName)
  const sourceName = getFilenameFromPathLike(request.sourcePath)
    ?? getFilenameFromPathLike(request.sourceUrl)
  const baseName = suggestedName ?? sourceName ?? fallbackBaseName

  const extension = getExtensionFromPathLike(suggestedName ?? undefined)
    ?? getExtensionFromPathLike(request.sourcePath)
    ?? getExtensionFromPathLike(request.sourceUrl)
    ?? getExtensionFromDataUrl(request.dataUrl)

  return ensureFilenameExtension(baseName, extension)
}

export function downloadAssetViaAnchor(
  request: DownloadAssetRequest,
  fallbackBaseName = 'image',
): void {
  const href = request.dataUrl ?? request.sourceUrl
  if (!href) {
    throw new Error('No downloadable browser URL is available')
  }

  const anchor = document.createElement('a')
  anchor.href = href
  anchor.download = inferDownloadFilename(request, fallbackBaseName)
  anchor.rel = 'noopener'
  anchor.click()
}
